import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import Moment from 'react-moment';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import { provideContext } from '../../../../context/context.jsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Button from '../../button/button.jsx';
import Message from '../../presentation/message/message.jsx';
import * as apiRentalSelectors from '../../../../redux-api/apiRental/apiRentalSelectors.js';
import * as apiUsersSelector from '../../../../redux-api/apiUsers/apiUsersSelectors.js';
import { Trans } from 'react-i18next';
import { apiGetUserMeRequestAction } from '../../../../redux-api/apiUsers/apiUsersActions.js';
import { bindActionCreators } from 'redux';

import './overviewPanel.scss';

class OverviewPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      focused: false,
      shippingDate: moment(props.shippingDate),
      canBeEdited: true,
    };
    this.announcerRef = React.createRef();
    this.dayPickerRef = React.createRef();
    this.previouslyFocusedElement = null;
  }

  announce = (message) => {
    if (this.announcerRef.current) {
      this.announcerRef.current.textContent = '';
      setTimeout(() => {
        if (this.announcerRef.current) {
          this.announcerRef.current.textContent = message;
        }
      }, 50);
    }
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event) => {
    if (this.state.isOpen && event.key === 'Escape') {
      this.closeCalendar();
    }
  };

  handleCalendarKeyDown = (e) => {
    const { shippingDate } = this.state;
    const newDate = moment(shippingDate);
    let dateChanged = false;

    const isMonthNavButton =
      (e.target.classList && e.target.classList.contains('DayPicker-NavButton')) ||
      (e.target.closest && e.target.closest('.DayPicker-NavButton')) ||
      (e.target.classList && e.target.classList.contains('DayPicker-Caption')) ||
      (e.target.closest && e.target.closest('.DayPicker-Caption'));

    if ((e.key === 'Enter' || e.key === ' ') && isMonthNavButton) {
      return;
    }

    switch (e.key) {
      case 'ArrowRight':
        newDate.add(1, 'day');
        if (newDate.day() === 0) newDate.add(1, 'day');
        if (newDate.day() === 6) newDate.add(2, 'day');
        dateChanged = true;
        e.preventDefault();
        break;
      case 'ArrowLeft':
        newDate.subtract(1, 'day');
        if (newDate.day() === 0) newDate.subtract(2, 'day');
        if (newDate.day() === 6) newDate.subtract(1, 'day');
        dateChanged = true;
        e.preventDefault();
        break;
      case 'ArrowUp':
        newDate.subtract(7, 'day');
        dateChanged = true;
        e.preventDefault();
        break;
      case 'ArrowDown':
        newDate.add(7, 'day');
        dateChanged = true;
        e.preventDefault();
        break;
      case 'Enter':
      case ' ': // Space key
        this.handleChangeShippingDate(newDate);
        e.preventDefault();
        return;
      case 'Escape':
        this.closeCalendar();
        e.preventDefault();
        return;
      default:
        return;
    }

    const today = moment();
    const fourMonthsFromNow = moment().add(4, 'months');

    if (
      newDate.isBefore(today, 'day') ||
      newDate.isAfter(fourMonthsFromNow, 'day') ||
      newDate.day() === 0 ||
      newDate.day() === 6
    ) {
      this.announce(`${newDate.format('MMMM D')} is not available for selection. Please choose another date.`);
      return;
    }

    if (dateChanged) {
      this.setState({
        shippingDate: newDate,
      });
    }
  };

  focusCalendar = () => {
    if (this.dayPickerRef && this.dayPickerRef.current) {
      const container = this.dayPickerRef.current.dayPicker;
      if (container) {
        const selectedDayElement = container.querySelector('.DayPicker-Day.DayPicker-Day--selected');
        if (selectedDayElement) {
          selectedDayElement.focus();
          this.announce('Calendar opened. Use arrow keys to navigate dates.');
        }
      }
    }
  };

  handleChangeShippingDate = (newDate) => {
    const { context, rental } = this.props;
    const formattedDate = moment(newDate).format('MMMM D, YYYY');
    const dayOfWeek = moment(newDate).format('dddd');

    this.setState({
      isOpen: false,
      focused: false,
      shippingDate: moment(newDate),
    });

    this.announce(`Shipping date selected: ${dayOfWeek}, ${formattedDate}. Your order will ship within 4 business days of this date.`);
    this.props.apiUpdateShippingDate({
      rentalId: rental._id,
      shippingDate: moment(newDate).format('YYYY-MM-DD'),
    });

    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  };

  handleCancelRentalClick = () => {
    const { rental } = this.props;
    this.props.showCancelOrderModal(rental);
  };

  handleEditAddressClick = () => {
    const { rental } = this.props;
    this.props.showEditAddressModal(rental);
  };

  openCalendar = (event) => {
    this.previouslyFocusedElement = document.activeElement;
    this.setState({
      isOpen: true,
      focused: true,
      announcement: 'Calendar opened. Use arrow keys to navigate and Enter to select a date.',
    }, () => {
      setTimeout(this.focusCalendar, 0);
    });
  };

  closeCalendar = () => {
    this.setState(
      {
        focused: false,
      },
      () => {
        if (this.previouslyFocusedElement) {
          this.previouslyFocusedElement.focus();
        }
      },
    );
    this.announce('Calendar closed. No date was selected.');
  };

  renderError = (errors) => {
    return (
      <div className="order-status-overview-error">
        <Message type="error">{errors.error.message}</Message>
      </div>
    );
  };

  filterDate = (date) => {
    const today = moment();
    const fourMonthsFromNow = moment().add(4, 'months');
    const momentDate = moment(date);

    if (momentDate.isBefore(today, 'day')) {
      return false;
    }

    if (momentDate.day() === 0 || momentDate.day() === 6) {
      return false;
    }

    if (momentDate.isAfter(fourMonthsFromNow, 'day')) {
      return false;
    }

    return true;
  };

  // Custom navigation component that prevents calendar from closing when Enter is pressed
  CustomNavbar = ({ onPreviousClick, onNextClick, className, showNextButton, showPreviousButton }) => (
    <div className={className} role="navigation">
      {showPreviousButton && (
        <button
          className="DayPicker-NavButton DayPicker-NavButton--prev"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPreviousClick();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onPreviousClick();
            }
          }}
          aria-label="Previous month"
          type="button"
        />
      )}
      {showNextButton && (
        <button
          className="DayPicker-NavButton DayPicker-NavButton--next"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNextClick();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onNextClick();
            }
          }}
          aria-label="Next month"
          type="button"
        />
      )}
    </div>
  );

  render() {
    const {
      t,
      context,
      userData,
      title,
      iconName,
      showButton,
      showShippingDate,
      itemData,
      order,
      successShippingDateUpdate,
      errorShippingDateUpdate,
    } = this.props;

    const { rental } = order;

    const { givenName, surname } = this.props.user;
    const { firstName, lastName, address1, address2, city, zip, province, country } = rental.shippingAddress;

    const userFullName = !givenName || !surname ? null : `${givenName} ${surname}`;
    const fullName = `${firstName} ${lastName}`;
    const shippingAddress = `${address1}, ${address2 ? address2 + ', ' : ''}${city} ${zip}, ${
      province ? province + ',' : ''
    } ${country}`;

    return (
      <div className="OverviewPanel">
        <div
          ref={this.announcerRef}
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
        />
        <div className="edit-profile-overview">
          {rental && rental.shippingAddressCanBeEdited && (
            <button className="edit-icon" onClick={this.handleEditAddressClick}>
              <FontAwesomeIcon icon={faEdit} size={'lg'} />
            </button>
          )}
          <div className="item">
            <h4>{t('overview_panel_name', 'Name')}</h4>
            <p>{userFullName || fullName}</p>
          </div>
          <div className="item">
            <h4>{t('global_email')}</h4>
            <p className="email">{userData.email}</p>
          </div>
          <div className="item">
            <h4>{t('global_shipping_address', 'Shipping address')}</h4>
            <p>{shippingAddress}</p>
          </div>
        </div>
        <div className="order-status-overview">
          <div className="icon-wrap">
            <div className="icon-center">
              <div className="icon active">
                <div className="circle">
                  <img src={iconName} className="active" alt="" />
                </div>
                <div className="label">
                  <h4>{title}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="details-wrap">
            <div className="details-center">
              <div className="details">
                {itemData && itemData.map((item, index) => {
                  return (
                    <div key={index} className="details-item">
                      <h4>{item.title}</h4>
                      <p>{item.value}</p>
                    </div>
                  );
                })}

                {showShippingDate && (
                  <div className="details-item">
                    <h4>{t('overview_panel_shipping_date_title', 'Shipping date')}</h4>
                    {!isEmpty(successShippingDateUpdate) && successShippingDateUpdate._id === rental.id && (
                      <p className="success-message">
                        {t(
                          'overview_pannel_shipping_date_changed_success',
                          'You have successfully changed the shipping date.',
                        )}
                      </p>
                    )}
                    {!isEmpty(errorShippingDateUpdate) && (
                      <p className="error-message">
                        {t('overview_panel_shipping_date_not_changed', 'You did not change shipping date')}
                      </p>
                    )}
                    {!this.state.focused ? (
                      <div className="shipping-date-display">
                        <span>
                          <Moment format={context.apiConfig.date.format} date={this.state.shippingDate} />
                        </span>
                        {rental && rental.canBeEdited && (
                          <button
                            className="edit-icon"
                            onClick={this.openCalendar}
                            aria-label={`Edit shipping date. Current date is ${moment(this.state.shippingDate).format('dddd, MMMM D, YYYY')}`}
                            aria-expanded={this.state.focused}
                            aria-controls="shipping-date-calendar"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="day-picker-container" id="shipping-date-calendar">
                        <div className="sr-only" id="calendar-instructions">
                          Use arrow keys to navigate days: left and right arrows move by day, up and down arrows move by
                          week. Press Enter or Space to select a date. Press Escape to close the calendar without making
                          a selection. Only weekdays within the next four months are available for selection. Weekends
                          and past dates are disabled. The currently focused date will be announced as you navigate.
                        </div>

                        <DayPicker
                          ref={this.dayPickerRef}
                          selectedDays={this.state.shippingDate.toDate()}
                          month={this.state.shippingDate.toDate()}
                          onDayClick={(day, { disabled }) => {
                            if (!disabled) {
                              this.handleChangeShippingDate(moment(day));
                            } else {
                              this.announce(`${moment(day).format('MMMM D')} is not available for selection.`);
                            }
                          }}
                          disabledDays={[
                            { daysOfWeek: [0, 6] },
                            { before: new Date() },
                            { after: moment().add(4, 'months').toDate() }
                          ]}
                          fromMonth={new Date()}
                          toMonth={moment().add(4, 'months').toDate()}
                          firstDayOfWeek={1}
                          aria-label="Shipping date calendar"
                          aria-describedby="calendar-instructions"
                          onKeyDown={this.handleCalendarKeyDown}
                          containerProps={{
                            tabIndex: 0,
                            className: 'calendar-container-accessible',
                            role: 'application',
                            'aria-roledescription': 'calendar'
                          }}
                          navbarElement={this.CustomNavbar}
                          renderDay={(day) => {
                            const date = day.getDate();
                            const isSelected = moment(day).isSame(this.state.shippingDate, 'day');
                            const isToday = moment(day).isSame(moment(), 'day');
                            const isDisabled = !this.filterDate(day);

                            return (
                              <div
                                role="gridcell"
                                tabIndex={-1}
                                aria-selected={isSelected}
                                aria-disabled={isDisabled}
                                aria-current={isToday ? 'date' : undefined}
                              >
                                {date}
                              </div>
                            );
                          }}
                        />
                        <div className="day-picker-footer">
                          <button
                            className="close-calendar-button"
                            onClick={this.closeCalendar}
                            aria-label="Close calendar without selecting a date"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}

                    <p className="shipping-date-disclaimer">
                      <Trans i18nKey="overview_panel_shipping_date_allow_two_to_five">
                        Ships within 4 business days of the date you pick. Allow an
                        {' '}
                        <i>additional</i>
                        {' '}
                        2-5 business days for SNOO to arrive.
                      </Trans>
                    </p>
                  </div>
                )}
                {showButton && order && (
                  <div className="details-item">
                    <Button color={'green'} to={`${context.appRoutes.BILLS.url}/${order._id}/${order.currentPeriod}`}>
                      {t('button_text_go_to_bill_history', 'Go to Bill History')}
                    </Button>
                  </div>
                )}
                {rental && rental.canBeEdited && (
                  <div className="details-item">
                    <button className="danger-text cancel-order-action" onClick={this.handleCancelRentalClick}>
                      {t('button_text_cancel_order', 'Cancel order')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  successShippingDateUpdate: apiRentalSelectors.getUpdateShippingDateSuccess(state),
  errorShippingDateUpdate: apiRentalSelectors.getUpdateShippingDateErrors(state),
  user: apiUsersSelector.getUser(state),
});

const mapDispatchToProps = dispatch => {
  const actions = bindActionCreators(
    {
      getUser: apiGetUserMeRequestAction,
    },
    dispatch,
  );
  return { actions };
};

OverviewPanel.propTypes = {
  t: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired,
  shippingDate: PropTypes.string,
  showCancelOrderModal: PropTypes.func,
  showEditAddressModal: PropTypes.func,
};

export default provideContext(connect(mapStateToProps, mapDispatchToProps)(OverviewPanel));
